import { useState, useEffect } from "react";
import { api } from "../utils/api";
import {
  Search, Users, ShoppingBag, Ban, CheckCircle2,
  RefreshCw, Check, Phone, Mail, Calendar,
} from "lucide-react";

const T = {
  primary:"#f97316", primaryLight:"#fff7ed", primaryDark:"#c2410c",
  white:"#ffffff", bg:"#f8fafc", card:"#ffffff",
  text:"#0c0a09", textSub:"#57534e", textMuted:"#a8a29e",
  border:"#e7e5e4", borderLight:"#f5f5f4",
  green:"#16a34a", greenLight:"#f0fdf4", greenDark:"#15803d",
  red:"#dc2626",   redLight:"#fef2f2",
  blue:"#2563eb",  blueLight:"#eff6ff",
  yellow:"#d97706",yellowLight:"#fffbeb",
  purple:"#7c3aed",purpleLight:"#faf5ff",
  shadow:"0 1px 3px rgba(0,0,0,0.06)",
  shadowMd:"0 4px 16px rgba(0,0,0,0.08)",
};

const LOYALTY_COLORS = {
  Bronze:  { c:"#92400e", bg:"#fef3c7" },
  Silver:  { c:"#475569", bg:"#f1f5f9" },
  Gold:    { c:"#b45309", bg:"#fef9c3" },
  Platinum:{ c:"#6d28d9", bg:"#f5f3ff" },
};

const Skeleton = ({ w="100%", h=16, r=6 }) => (
  <div style={{ width:w, height:h, borderRadius:r, background:"linear-gradient(90deg,#f5f5f4 25%,#e7e5e4 50%,#f5f5f4 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }}/>
);

export default function UsersPage() {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [toast,   setToast]   = useState("");
  const [error,   setError]   = useState("");

  const showToast = (m, isErr=false) => {
    setToast({ msg:m, err:isErr });
    setTimeout(() => setToast(""), 2500);
  };

  const load = async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ page, limit:20 });
      if (search.trim()) params.append("search", search.trim());

      const res = await api.users.getAll(params.toString());
      console.log("Users API response:", res); // debug

      if (res.success) {
        // Backend returns { success, total, data: [...] }
        setUsers(res.data || []);
        setTotal(res.total || res.data?.length || 0);
      } else {
        setError(res.message || "Failed to load users");
        setUsers([]);
      }
    } catch(e) {
      setError("Backend connect nahi hua: " + e.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page]);

  // Search on Enter or when search is cleared
  useEffect(() => {
    if (search === "") { setPage(1); load(); }
  }, [search]);

  const handleSearch = () => { setPage(1); load(); };

  const handleToggle = async (id) => {
    try {
      const res = await api.users.toggle(id);
      if (res.success) {
        setUsers(u => u.map(x => x._id === id ? (res.data || {...x, isActive:!x.isActive}) : x));
        showToast(res.message || "Updated!");
      } else {
        showToast(res.message || "Error!", true);
      }
    } catch(e) {
      showToast("Network error!", true);
    }
  };

  const stats = [
    { l:"Total Customers", v:total,                                icon:Users,        color:T.primary, bg:T.primaryLight },
    { l:"Active",          v:users.filter(u=>u.isActive).length,   icon:CheckCircle2, color:T.green,   bg:T.greenLight   },
    { l:"Blocked",         v:users.filter(u=>!u.isActive).length,  icon:Ban,          color:T.red,     bg:T.redLight     },
    { l:"Total Orders",    v:users.reduce((s,u)=>s+(u.totalOrders||0),0), icon:ShoppingBag, color:T.blue, bg:T.blueLight },
  ];

  return (
    <div style={{ fontFamily:"'DM Sans','Plus Jakarta Sans',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@500&display=swap');
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .spin{animation:spin 1s linear infinite}
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", top:80, left:"50%", transform:"translateX(-50%)", background:toast.err?T.red:T.text, color:"#fff", padding:"10px 22px", borderRadius:100, fontSize:"0.82rem", fontWeight:600, zIndex:999, display:"flex", alignItems:"center", gap:8, boxShadow:T.shadowMd, animation:"fadeUp .2s ease" }}>
          <Check size={14}/> {toast.msg}
        </div>
      )}

      {/* Page Header */}
      <div style={{ marginBottom:22, animation:"fadeUp .4s ease both" }}>
        <div style={{ fontFamily:"'DM Serif Display',Georgia,serif", fontSize:"1.4rem", color:T.text }}>Customers</div>
        <div style={{ fontSize:"0.75rem", color:T.textMuted, marginTop:3 }}>Manage all registered customers</div>
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginBottom:20 }}>
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} style={{ background:s.bg, borderRadius:14, border:`1px solid ${s.color}22`, padding:"16px 18px", animation:`fadeUp .4s ${i*.06}s ease both` }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                <Icon size={16} color={s.color} strokeWidth={1.8}/>
              </div>
              {loading
                ? <Skeleton h={28} w="50%" r={6}/>
                : <div style={{ fontFamily:"'DM Serif Display',Georgia,serif", fontSize:"1.8rem", color:s.color, lineHeight:1 }}>{s.v}</div>
              }
              <div style={{ fontSize:"0.7rem", fontWeight:600, color:T.textSub, marginTop:5 }}>{s.l}</div>
            </div>
          );
        })}
      </div>

      {/* Search + Refresh */}
      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap", animation:"fadeUp .4s .1s ease both" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, background:T.card, border:`1.5px solid ${T.border}`, borderRadius:11, padding:"9px 14px", flex:1, maxWidth:360, transition:"border-color .2s" }}
          onFocusCapture={e => e.currentTarget.style.borderColor=T.primary}
          onBlurCapture={e => e.currentTarget.style.borderColor=T.border}>
          <Search size={14} color={T.textMuted}/>
          <input
            placeholder="Search by name, phone, email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
            style={{ border:"none", background:"transparent", color:T.text, fontSize:"0.82rem", outline:"none", fontFamily:"inherit", width:"100%" }}
          />
        </div>
        <button onClick={handleSearch} style={{ background:T.primary, border:"none", borderRadius:11, padding:"9px 18px", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontSize:"0.8rem", fontWeight:700, color:"#fff", fontFamily:"inherit" }}>
          <Search size={13}/> Search
        </button>
        <button onClick={() => { setPage(1); load(); }} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:11, padding:"9px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontSize:"0.8rem", fontWeight:600, color:T.textSub, fontFamily:"inherit" }}>
          <RefreshCw size={13} className={loading?"spin":""}/> Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background:T.redLight, border:"1px solid #fecaca", borderRadius:12, padding:"12px 18px", marginBottom:16, fontSize:"0.8rem", color:T.red, fontWeight:600 }}>
          ⚠️ {error}
        </div>
      )}

      {/* Table */}
      <div style={{ background:T.card, borderRadius:16, border:`1px solid ${T.border}`, overflow:"hidden", boxShadow:T.shadow, animation:"fadeUp .5s .15s ease both" }}>
        {loading ? (
          <div style={{ padding:"24px 24px" }}>
            {Array(6).fill(0).map((_,i) => (
              <div key={i} style={{ display:"flex", gap:14, marginBottom:18, alignItems:"center" }}>
                <div style={{ width:38, height:38, borderRadius:"50%", background:"#f5f5f4", flexShrink:0 }}/>
                <Skeleton w={120} h={14} r={4}/>
                <Skeleton w={90} h={14} r={4}/>
                <Skeleton w={50} h={14} r={4}/>
                <Skeleton w={60} h={14} r={4}/>
                <Skeleton w={70} h={22} r={100}/>
              </div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding:70, textAlign:"center" }}>
            <Users size={42} color={T.border} style={{ margin:"0 auto 14px", display:"block" }}/>
            <div style={{ fontWeight:700, fontSize:"0.9rem", color:T.textSub, marginBottom:4 }}>Koi customers nahi mile</div>
            <div style={{ fontSize:"0.75rem", color:T.textMuted }}>Search change karo ya refresh karo</div>
          </div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", minWidth:680 }}>
              <thead>
                <tr style={{ background:T.bg, borderBottom:`1px solid ${T.border}` }}>
                  {["Customer","Phone","Orders","Spent","Loyalty","Joined","Status","Action"].map(h => (
                    <th key={h} style={{ padding:"11px 18px", textAlign:"left", fontSize:"0.62rem", fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"0.8px", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => {
                  const loyalty = u.loyaltyLevel || "Bronze";
                  const lc = LOYALTY_COLORS[loyalty] || LOYALTY_COLORS.Bronze;
                  return (
                    <tr key={u._id}
                      style={{ borderBottom:`1px solid ${T.borderLight}`, transition:"background .15s", animation:`fadeUp .3s ${i*.04}s ease both` }}
                      onMouseEnter={e => e.currentTarget.style.background = T.bg}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>

                      {/* Customer */}
                      <td style={{ padding:"13px 18px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <div style={{ width:36, height:36, borderRadius:"50%", background:`linear-gradient(135deg,${T.primary},#fb923c)`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                            <span style={{ fontFamily:"'DM Serif Display',Georgia,serif", color:"#fff", fontSize:"1rem" }}>{u.name?.charAt(0)?.toUpperCase()||"?"}</span>
                          </div>
                          <div>
                            <div style={{ fontWeight:700, fontSize:"0.84rem", color:T.text }}>{u.name||"—"}</div>
                            {u.email && <div style={{ fontSize:"0.68rem", color:T.textMuted, display:"flex", alignItems:"center", gap:3 }}><Mail size={9}/>{u.email}</div>}
                          </div>
                        </div>
                      </td>

                      {/* Phone */}
                      <td style={{ padding:"13px 18px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:"0.78rem", color:T.textSub, fontFamily:"'DM Mono',monospace" }}>
                          <Phone size={11} color={T.textMuted}/>{u.phone||"—"}
                        </div>
                      </td>

                      {/* Orders */}
                      <td style={{ padding:"13px 18px" }}>
                        <span style={{ fontFamily:"'DM Serif Display',Georgia,serif", fontSize:"1.1rem", color:T.text }}>{u.totalOrders||0}</span>
                      </td>

                      {/* Spent */}
                      <td style={{ padding:"13px 18px" }}>
                        <span style={{ fontWeight:700, fontSize:"0.84rem", color:T.green }}>₹{u.totalSpent||u.savedAmount||0}</span>
                      </td>

                      {/* Loyalty */}
                      <td style={{ padding:"13px 18px" }}>
                        <span style={{ background:lc.bg, color:lc.c, fontSize:"0.68rem", fontWeight:700, padding:"4px 10px", borderRadius:100, border:`1px solid ${lc.c}22` }}>{loyalty}</span>
                      </td>

                      {/* Joined */}
                      <td style={{ padding:"13px 18px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:"0.72rem", color:T.textMuted }}>
                          <Calendar size={11}/>{new Date(u.createdAt).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
                        </div>
                      </td>

                      {/* Status */}
                      <td style={{ padding:"13px 18px" }}>
                        <span style={{ background:u.isActive?T.greenLight:T.redLight, color:u.isActive?T.greenDark:T.red, fontSize:"0.68rem", fontWeight:700, padding:"4px 10px", borderRadius:100, border:`1px solid ${u.isActive?"#bbf7d0":"#fecaca"}` }}>
                          {u.isActive ? "Active" : "Blocked"}
                        </span>
                      </td>

                      {/* Action */}
                      <td style={{ padding:"13px 18px" }}>
                        <button onClick={() => handleToggle(u._id)} style={{
                          background: u.isActive ? T.redLight : T.greenLight,
                          border: `1px solid ${u.isActive?"#fecaca":"#bbf7d0"}`,
                          borderRadius:9, padding:"6px 14px",
                          color: u.isActive ? T.red : T.greenDark,
                          fontWeight:700, fontSize:"0.72rem",
                          cursor:"pointer", fontFamily:"inherit",
                          display:"flex", alignItems:"center", gap:5,
                          transition:"all .2s",
                        }}>
                          {u.isActive ? <Ban size={11}/> : <CheckCircle2 size={11}/>}
                          {u.isActive ? "Block" : "Unblock"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 20 && (
        <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:8, marginTop:16 }}>
          <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
            style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:9, padding:"7px 16px", cursor:page===1?"not-allowed":"pointer", fontFamily:"inherit", fontSize:"0.78rem", color:T.textSub, opacity:page===1?.5:1 }}>
            ← Prev
          </button>
          <span style={{ padding:"7px 16px", fontSize:"0.78rem", color:T.textSub, fontWeight:600 }}>
            Page {page} · {total} customers
          </span>
          <button onClick={() => setPage(p=>p+1)} disabled={users.length < 20}
            style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:9, padding:"7px 16px", cursor:users.length<20?"not-allowed":"pointer", fontFamily:"inherit", fontSize:"0.78rem", color:T.textSub, opacity:users.length<20?.5:1 }}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}